import {Component, DestroyRef, DoCheck, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {PATTERNS} from '../../../common/constants';
import {TenureService} from '../tenure.service';
import {LookupService} from '../../../common/service/lookup.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {PlanPurposeLookup} from '../../../common/models/plan-purpose.model';
import {ParentPlanType} from '../../../common/models/parent-plan-type.model';
import {FormerTenureUiConfig} from '../../../common/models/former-tenure-ui-config';
import {NgEventBus} from 'ng-event-bus';
import { NgClass } from '@angular/common';
import {ControlContainer, FormsModule, NgForm, NgModel, ReactiveFormsModule} from '@angular/forms';
import {NlrHasPermissionsPipe} from '../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../common/pipe/nlr-permissions-disabled-pipe';
import {TenureNewLotDetailComponent} from '../tenure-new-lot-detail/tenure-new-lot-detail.component';
import {NlrPatternDirective} from '../../../common/directive/nlr-pattern.directive';
import {Tenure} from '../../../common/models/tenure-model';
import {NlrFormFieldHasErrorPipe} from '../../../common/pipe/nlr-form-field-has-error.pipe';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-tenure-parent-lots',
    imports: [
    NgClass,
    ReactiveFormsModule,
    FormsModule,
    NlrHasPermissionsPipe,
    NlrPermissionsDisabledPipe,
    TenureNewLotDetailComponent,
    NlrPatternDirective,
    NlrFormFieldHasErrorPipe
],
    templateUrl: './tenure-parent-lots.component.html',
    styleUrl: './tenure-parent-lots.component.scss',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class TenureParentLotsComponent implements OnInit, OnChanges, DoCheck {
    @Input() tenure: Tenure;
    @Input() label: string;
    @Input() disabled: false;
    @Input() submitted: boolean;
    @Input() tenureNewLotsLandAttrs: boolean;
    private planPurposeLookups: PlanPurposeLookup[];
    private parentPlanTypeLookups: ParentPlanType[];
    private formerTenureUIConfigLookup: FormerTenureUiConfig[];
    @Input() name: string;
    @ViewChildren(NgModel) inputModels!: QueryList<NgModel>;

    constructor(
        private tenureService: TenureService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.init();
        this.eventBus
            .on('add-new-tenure')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.markAllInputsAsUntouched();
            });
    }

    markAllInputsAsUntouched() {
        this.inputModels.forEach(model => {
            model.control.markAsUntouched();
            model.control.markAsPristine();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tenure']) {
            this.init();
        }
    }

    title = 'PlanInformationTenureParentLotsController';
    showNonLotCommonProperty = false;

    lotPattern = PATTERNS.PARENT_LOT_PATTERN;
    nonLotCommonPropertyValues = [
        {value: true, displayValue: 'Yes', toolTip: 'Yes'},
        {value: false, displayValue: 'No', toolTip: 'No'}
    ];

    nonLotCommonPropertylabel = '';
    nonLotCommonPropertyIdentifier = '';

    addLot(idx: number) {
        this.tenure.lots.splice(idx + 1, 0, this.newParentLot());
    }

    removeLot(idx: number) {
        this.tenure.lots.splice(idx, 1);
        if (this.tenure.lots.length === 0) {
            this.tenure.lots.push(this.newParentLot());
        }
    }

    lotVerified(idx: number) {
        const parentLotResponse = this.getParentLotResponse(idx);
        return parentLotResponse && parentLotResponse.lot;
    }

    lotVerifyError(idx: number) {
        const parentLotResponse = this.getParentLotResponse(idx);
        if (parentLotResponse) {
            return parentLotResponse.error;
        }
    }

    duplicateNonLotCommonProperty(idx: number) {
        return this.tenure.lots[idx].nonLotCommonProperty && this.tenure.lots.filter(l => l.nonLotCommonProperty).length > 1;
    }

    nonLotCommonPropertyVerified(idx: number) {
        const parentLotResponse = this.getNonLotCommonPropertyResponse(idx);
        if (parentLotResponse && !parentLotResponse.error) {
            return parentLotResponse && parentLotResponse.response;
        }
    }

    nonLotCommonPropertyVerifyError(idx: number) {
        if (!this.duplicateNonLotCommonProperty(idx)) {
            const parentLotResponse = this.getNonLotCommonPropertyResponse(idx);
            if (parentLotResponse) {
                return parentLotResponse.error;
            }
        }
    }

    init() {
        if (!this.tenure.lots) {
            this.tenure.lots = [];
        }

        if (this.tenure.lots.length === 0) {
            this.tenure.lots.push(this.newParentLot());
        }

        this.populateParentPlanTypeLookups();
        this.populateFormerTenureUIConfigLookups();
        this.populatePlanPurposeLookups();
    }

    //TODO
    parentLotIdentifier: any;

    trackByIndex(index: number): number {
        return index;
    }

    ngDoCheck(): void {
        if (this.parentPlanTypeLookups) {
            for (let i = 0; i < this.parentPlanTypeLookups.length; i++) {
                if (
                    this.tenure.parentPlanType &&
                    this.parentPlanTypeLookups[i].code === this.tenure.parentPlanType &&
                    this.parentPlanTypeLookups[i].manualCommonPropertyLotTypeSelection &&
                    this.getFormerTenureUIConfigLookup().commonPropertyLabel
                ) {
                    this.showNonLotCommonProperty = true;
                    this.nonLotCommonPropertylabel = this.getFormerTenureUIConfigLookup().commonPropertyLabel;
                    this.nonLotCommonPropertyIdentifier = this.parentPlanTypeLookups[i].manualCommonPropertyLotTypeSelection;
                    return;
                } else {
                    this.showNonLotCommonProperty = false;
                }
            }
        }
    }

    populateParentPlanTypeLookups() {
        console.debug(this.title + ' controller ParentPlanTypeLookup');
        this.parentPlanTypeLookups = this.lookupService.getLookups('parentPlanType');
    }

    populateFormerTenureUIConfigLookups() {
        console.debug(this.title + ' controller formerTenureUIConfigLookup');
        this.formerTenureUIConfigLookup = this.lookupService.getLookups('formerTenureUiConfig');
    }

    getFormerTenureUIConfigLookup() {
        return this.formerTenureUIConfigLookup.filter(
            lookup => lookup.parentTenureAssociationType === this.getPlanPurposesLookup().parentTenureAssociationType
        )[0];
    }

    getPlanPurposesLookup() {
        return this.planPurposeLookups.filter(lookup => lookup.id === this.planModelService.getModel().planPurpose.id)[0];
    }

    populatePlanPurposeLookups() {
        console.debug(this.title + ' controller planPurposeLookup');

        this.planPurposeLookups = this.lookupService.getLookups('planPurpose');
    }

    getParentLotResponse(idx: number) {
        const parentLot = this.tenure.lots[idx];
        return this.tenureService.getParentLotResponse(this.tenure.parentPlanType, this.tenure.parentPlan, parentLot.parentLotIdentifier);
    }

    getNonLotCommonPropertyResponse(idx: number) {
        return this.tenureService.getNonLotCommonPropertyResponse(this.tenure.parentPlanType, this.tenure.parentPlan);
    }

    newParentLot() {
        return {
            parentLotIdentifier: '',
            newLotDetails: {
                fullySupersededByRoad: false,
                newLots: []
            },
            nonLotCommonProperty: false
        };
    }
}
